'use client';

import { useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';

import {
  Activity,
  ActivitySquare,
  Bell,
  Calendar,
  ChartLine,
  CircleAlert,
  Globe,
  Lock,
  MonitorCheck,
  Palette,
  PanelLeft,
  Timer,
  Users,
  Wrench,
  Zap,
} from 'lucide-react';
import { motion } from 'motion/react';

import { Heading } from '@/components/heading';
import { QuickCreateForm } from '@/components/quick-create-form';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

const faqs = [
  {
    question: 'What is Statusly?',
    answer:
      'Statusly is a simple, elegant uptime monitoring and public status page service built for indie developers. It helps you track your site’s availability and share real-time status with users.',
  },
  {
    question: 'Is Statusly really free?',
    answer:
      'Yes! During our beta, Statusly is completely free to use, and we intend to always offer a free Hobby plan into the future. You can create monitors, share public status pages, and receive downtime alerts with no cost.',
  },
  {
    question: 'Do I need to create an account to try it?',
    answer:
      'No — you can generate a demo status page and test monitor functionality without registering. If you want to save your work long-term or manage more than a few monitors, you’ll need to sign up.',
  },
  {
    question: 'How often are monitors checked?',
    answer:
      'In the free beta, each monitor is checked from a single region every 5 minutes. Faster and multi-region checks will be available in premium plans later.',
  },
  {
    question: 'How quickly will I be notified if my service/site goes down?',
    answer:
      'With our free beta, monitor checks run every 5 minutes, and you’ll be sent a notification within seconds of detecting downtime.',
  },
  {
    question: 'What types of requests can I monitor?',
    answer:
      'Currently, free beta monitors support HEAD and GET requests with a maximum timeout of 15 seconds.',
  },
  {
    question: 'What happens to anonymous status pages and monitors?',
    answer:
      'If you don’t register an account, your anonymous monitors and status pages will be automatically deleted after 7 days.',
  },
  {
    question: 'How many monitors and status pages can I create?',
    answer:
      'During the beta, free users can create up to 5 monitors and 3 status pages per account.',
  },
  {
    question: 'Can I customize my status page?',
    answer:
      'Yes — your public status page includes your monitor names, history, and current incident status. More customization options are coming soon.',
  },
  {
    question: 'Is my data private and secure?',
    answer:
      'We take your privacy seriously. We don’t share or sell your data, and all stored information is protected using industry best practices. Review our Privacy Policy for full details.',
  },
  {
    question: 'Can I invite teammates or set up a team account?',
    answer:
      'Team and organization features are not available during the beta, but are on our roadmap for future releases.',
  },
  {
    question: 'Why are you running a free beta?',
    answer:
      'We’re stress-testing reliability and learning what actually matters to indie developers. We’re looking for people who are willing to use the product and tell us what’s confusing, missing, or delightful. As a thank-you, active beta testers get free access to premium plans post-beta.',
  },
  {
    question: 'Will my monitors continue working after the beta ends?',
    answer:
      'We’ll notify users before the beta ends and offer migration options to premium or expanded free plans. No surprise shutdowns.',
  },
];

function PulseCircle() {
  return (
    <div className="bg-healthy relative h-2.5 w-2.5 rounded-full">
      <div className="bg-healthy absolute h-full w-full animate-ping rounded-full" />
    </div>
  );
}

const parentAnimationFrame = {
  hidden: {
    opacity: 0,
  },
  show: {
    opacity: 1,
    transition: {
      delayChildren: 1,
      staggerChildren: 1,
    },
  },
};

const fadeIn1 = {
  hidden: {
    opacity: 0,
    x: 50,
    y: -50,
    skewX: -10,
    skewY: 1,
    rotate: 2,
    boxShadow: '0px 0px 0px 0px rgba(0,0,0,0)',
  },
  show: {
    opacity: 1,
    x: 0,
    y: 0,
    boxShadow: '0 0 175px -10px rgba(235, 150, 204, 0.8)',
    transition: {
      duration: 1, // for opacity/x/y
      boxShadow: {
        delay: 1,
        duration: 2,
      },
    },
  },
};

const fadeIn2 = {
  hidden: {
    opacity: 0,
    x: 350,
    y: 180,
    skewX: -10,
    skewY: 1,
    rotate: 2,
    boxShadow: '0px 0px 0px 0px rgba(0,0,0,0)', // no shadow initially
  },
  show: {
    opacity: 1,
    x: 300,
    y: 230,
    boxShadow: '-10px 5px 40px 0px rgba(0, 0, 0, 0.8) ',
    transition: {
      duration: 1,
      boxShadow: {
        delay: 2,
        duration: 2,
      },
    },
  },
};

export default function HomePage() {
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash;
      if (!hash) {
        return;
      }

      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    };

    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);

    return () => {
      window.removeEventListener('hashchange', scrollToHash);
    };
  }, []);

  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden px-6 lg:px-10">
        <div className="relative z-1 mx-auto max-w-7xl py-[4%] lg:py-38">
          <div className="flex flex-col gap-8 lg:grid lg:grid-cols-12">
            <div className="flex flex-col items-center gap-4 text-center md:mx-auto md:max-w-2xl lg:col-span-6 lg:items-start lg:text-left">
              <Badge variant="secondary" className="text-healthy flex gap-2">
                <PulseCircle />
                <span>Now in beta - completely free</span>
              </Badge>
              <Heading
                level={1}
                size={1}
                className="text-4xl font-extrabold tracking-tight sm:text-5xl md:!text-6xl"
              >
                Status Pages
                <span className="text-healthy mt-2 block">Made Simple</span>
              </Heading>
              <h3 className="mb-7 font-normal sm:text-xl lg:text-lg xl:text-xl">
                Enter a URL and get a live status page in seconds.
                <br />
                No signup required.
              </h3>
              <QuickCreateForm />
              <p className="text-accent-foreground -mt-2 w-full text-center text-sm">
                No registration required - we'll keep it live for 7 days.
                <br />
                Claim it anytime by signing up.
                <br />
              </p>
            </div>
          </div>
        </div>
        <motion.div
          variants={parentAnimationFrame}
          initial="hidden"
          animate="show"
          className="aspect-4/3 lg:absolute lg:top-[25%] lg:-right-[2%] lg:-bottom-[10%] lg:w-[50%] lg:max-w-3xl"
          // className="absolute left-[55%] top-[25%] -bottom-[10%] -right-[4%]" (alternative look)
        >
          <motion.div
            variants={fadeIn1}
            className="absolute h-full w-full overflow-hidden rounded-lg"
          >
            <Image
              fill
              src="/statusly-screenshot-single-monitor-2-9-26.png"
              alt="Screenshot from the admin dashboard"
              className="object-contain object-top-left lg:object-cover"
            />
          </motion.div>
          <motion.div
            variants={fadeIn2}
            className="absolute h-full w-full transform overflow-hidden rounded-lg max-md:hidden"
          >
            <Image
              fill
              src="/statusly-status-page-dark.png"
              alt="Screenshot from the admin dashboard"
              className="object-contain object-top-left lg:object-cover"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* FEAURES */}
      <section
        id="features"
        className="bg-secondary flex w-full flex-col gap-18 px-6 py-24 lg:px-10"
      >
        <div className="flex flex-col items-center gap-1 text-center">
          <Heading level={4} size={4} className="text-healthy tracking-wide uppercase">
            Features
          </Heading>
          <Heading level={2} size={2} className="">
            Everything you need, nothing you don't
          </Heading>
          <p className="text-accent-foreground max-w-2xl text-lg">
            Statusly provides powerful uptime monitoring without the complexity. Perfect for indie
            developers and small teams.
          </p>
        </div>
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-12">
            <div className="flex gap-4">
              <div className="bg-background flex h-12 w-12 items-center justify-center rounded-md">
                <Activity className="stroke-healthy" />
              </div>
              <div className="flex flex-1 flex-col gap-2">
                <Heading level={5} size={5}>
                  Real-time Monitoring
                </Heading>
                <p className="text-accent-foreground">
                  Get instant notifications when your services go down, with accurate uptime
                  tracking.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-background flex h-12 w-12 items-center justify-center rounded-md">
                <PanelLeft className="stroke-healthy" />
              </div>
              <div className="flex flex-1 flex-col gap-2">
                <Heading level={5} size={5}>
                  Beautiful Status Pages
                </Heading>
                <p className="text-accent-foreground">
                  Professionally designed status pages that match your brand and keep your users
                  informed.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-background flex h-12 w-12 items-center justify-center rounded-md">
                <Bell className="stroke-healthy" />
              </div>
              <div className="flex flex-1 flex-col gap-2">
                <Heading level={5} size={5}>
                  Instant Notifications
                </Heading>
                <p className="text-accent-foreground">
                  Get instant alerts via email (Slack, SMS, webhook integrations coming soon).
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-background flex h-12 w-12 items-center justify-center rounded-md">
                <Zap className="stroke-healthy" />
              </div>
              <div className="flex flex-1 flex-col gap-2">
                <Heading level={5} size={5}>
                  One-Click Setup
                </Heading>
                <p className="text-accent-foreground">
                  No complex configuation. Sensible defaults means lightning-fast setup.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-background flex h-12 w-12 items-center justify-center rounded-md">
                <ChartLine className="stroke-healthy" />
              </div>
              <div className="flex flex-1 flex-col gap-2">
                <Heading level={5} size={5}>
                  Historical Status and Response Time Charts
                </Heading>
                <p className="text-accent-foreground">
                  Historical status and response time charts that look great.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-background flex h-12 w-12 items-center justify-center rounded-md">
                <Lock className="stroke-healthy" />
              </div>
              <div className="flex flex-1 flex-col gap-2">
                <Heading level={5} size={5}>
                  Secure By Default
                </Heading>
                <p className="text-accent-foreground">
                  You data is protected with enterprise-grade security and regular backups.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section
        id="pricing"
        className="bg-background flex w-full flex-col gap-18 px-6 py-24 lg:px-10"
      >
        <div className="flex flex-col items-center gap-1 text-center">
          <Heading level={4} size={4} className="text-healthy tracking-wide uppercase">
            PRICING
          </Heading>
          <Heading level={2} size={2} className="">
            Free Beta
          </Heading>
          <p className="text-accent-foreground max-w-2xl text-lg">
            Try out our beta, share your feedback. Active beta testers who provide meaningful
            feedback will get free access to premium plans post-beta.
          </p>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-12">
            <Card className="border-healthy relative mt-20 w-full max-w-md text-left">
              <Badge className="bg-healthy text-background absolute top-0 left-7 -translate-y-1/2">
                Currently Available
              </Badge>
              <CardContent className="align-left flex flex-col gap-4 px-10 py-3">
                <Heading level={4} size={4}>
                  Free Beta
                </Heading>
                <Heading level={6} size={1} className="font-bold tracking-wide">
                  $0
                  <span className="text-accent-foreground ml-2 text-lg font-semibold tracking-normal">
                    /forever
                  </span>
                </Heading>
                <p className="text-accent-foreground text-sm">
                  Get early access to Statusly while we build the full project.
                </p>
                <ul className="my-2 space-y-4">
                  <li className="flex items-start gap-3">
                    <ActivitySquare className="stroke-healthy" />5 monitors
                  </li>
                  <li className="flex items-start gap-3">
                    <MonitorCheck className="stroke-healthy" />3 status pages
                  </li>
                  <li className="flex items-start gap-3">
                    <Timer className="stroke-healthy" />
                    Checks every 5 minutes
                  </li>
                  <li className="flex items-start gap-3">
                    <Globe className="stroke-healthy" />
                    Single-region checks
                  </li>
                  <li className="flex items-start gap-3">
                    <Calendar className="stroke-healthy" />
                    30-day data retention
                  </li>
                  <li className="flex items-start gap-3">
                    <Bell className="stroke-healthy" />
                    Email notifications
                  </li>
                </ul>
                <Separator />
                <div className="flex items-start gap-3 py-2 text-sm">
                  <CircleAlert className="stroke-warning" />
                  <p className="text-accent-foreground">
                    Paid plans coming soon with more monitors, faster checks, and additional
                    features
                  </p>
                </div>
                <Button variant="healthy" size="xl" asChild>
                  <Link href="/sign-up">JOIN THE FREE BETA</Link>
                </Button>
              </CardContent>
            </Card>

            <div className="p-[5%]">
              <Card className="border-accent-foreground relative mt-20 w-full max-w-md text-left opacity-90">
                <Badge className="bg-accent-foreground text-background absolute top-0 left-7 -translate-y-1/2">
                  Coming Soon
                </Badge>
                <CardContent className="align-left flex flex-col gap-4 px-10 py-3">
                  <Heading level={4} size={4}>
                    Premium Plans
                  </Heading>
                  <Heading level={6} size={1} className="font-bold tracking-wide">
                    $5-19
                    <span className="text-accent-foreground ml-2 text-lg font-semibold tracking-normal">
                      /month
                    </span>
                  </Heading>
                  <p className="text-accent-foreground text-sm">
                    In the near future, we hope to offer premium plans with additional features.
                  </p>
                  <ul className="my-2 space-y-4">
                    <li className="flex items-start gap-3">
                      <ActivitySquare className="stroke-healthy" />
                      100's of monitors
                    </li>
                    <li className="flex items-start gap-3">
                      <MonitorCheck className="stroke-healthy" />
                      Unlimited status pages
                    </li>
                    <li className="flex items-start gap-3">
                      <Timer className="stroke-healthy" />
                      Checks every 30s
                    </li>
                    <li className="flex items-start gap-3">
                      <Globe className="stroke-healthy" />
                      Multi-region checks
                    </li>
                    <li className="flex items-start gap-3">
                      <Calendar className="stroke-healthy" />
                      12-month data retention
                    </li>
                    <li className="flex items-start gap-3">
                      <Bell className="stroke-healthy" />
                      Slack, SMS, webhook notifications
                    </li>
                    <li className="flex items-start gap-3">
                      <Users className="stroke-healthy" />
                      Support for teams + members
                    </li>
                    <li className="flex items-start gap-3">
                      <Wrench className="stroke-healthy" />
                      Maintenance windows
                    </li>
                    <li className="flex items-start gap-3">
                      <Palette className="stroke-healthy" />
                      Custom branding + domains
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-secondary flex w-full flex-col gap-18 px-6 py-24 lg:px-10">
        <div className="flex flex-col items-center gap-1 text-center">
          <Heading level={4} size={4} className="text-healthy tracking-wide uppercase">
            FAQ
          </Heading>
          <Heading level={2} size={2} className="">
            Frequently Asked Questions
          </Heading>
          <p className="text-accent-foreground max-w-2xl text-lg">
            Some of the most common questions and nice-to-know's. If you can't find the answer
            you're looking for, <a href="mailto:support@statusly.com">ping us.</a>
          </p>
        </div>
        <div className="flex justify-center">
          <Accordion type="multiple" className="w-full max-w-3xl">
            {faqs.map(({ question, answer }, index) => {
              return (
                <AccordionItem value={question} key={index}>
                  <AccordionTrigger className="text-lg">{question}</AccordionTrigger>
                  <AccordionContent className="text-accent-foreground text-base">
                    {answer}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </section>
    </main>
  );
}
